import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { AssessmentDomainStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { AssessmentDetail } from '@libs/types';

import { useSaveDomain, useStartDomain } from '../api';
import { AssessmentDocumentViewModel } from '../types';
import {
  DomainDetailsViewModel,
  findDomainByOrderIndex,
  ProgressFlowViewModel,
} from '../utils/assessment-mapper';
import {
  canNavigateToDomain as checkCanNavigateToDomain,
  validateDomainBeforeSave,
  validateNextDomain,
} from '../utils/assessment-workflow-validators';
import { getLocalizedErrorMessage } from '../utils/error-utils';

import { NavigationGuard, NavigationType } from './useNavigationGuard';

interface UseDomainNavigationProps {
  assessmentId: string;
  assessment: AssessmentDetail | undefined;
  domainDetailsViewModel: DomainDetailsViewModel | undefined | null;
  progressFlowViewModel: ProgressFlowViewModel | undefined;
  selectedIndex: number;
  files: AssessmentDocumentViewModel[];
  noEvidenceChecked: boolean;
  isApplicable: boolean;
  setValidationError: (err: string | null) => void;
  navigationGuard: NavigationGuard;
}

/**
 * Domain navigation handlers for the assessment workflow.
 *
 * Owns the navigation mutations (`useSaveDomain`, `useStartDomain`) and the
 * "guard → maybe start domain → navigate" dance via `navigateToDomain`. Every
 * inter-domain navigation in the workflow goes through one of these handlers:
 *
 * - `handleBack` / `handleNext` — sequential nav
 * - `handleNodeClick` — stepper click
 * - `handleResume` / `handleResumeLater` — entry/exit
 * - `handleSaveAndNext` — save + advance (or finish on last domain)
 * - `handleEditDomain` — re-open a completed domain in place
 */
export const useDomainNavigation = ({
  assessmentId,
  assessment,
  domainDetailsViewModel,
  progressFlowViewModel,
  selectedIndex,
  files,
  noEvidenceChecked,
  isApplicable,
  setValidationError,
  navigationGuard,
}: UseDomainNavigationProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();

  const saveDomainMutation = useSaveDomain();
  const startDomainMutation = useStartDomain();

  /**
   * Common pattern: guard → if NOT_STARTED start the target domain →
   * navigate. Surfaces a localized error snackbar on failure.
   */
  const navigateToDomain = useCallback(
    (
      targetDomainId: string,
      targetDomainStatus: AssessmentDomainStatus | undefined,
      navigationType: NavigationType,
      errorFallback = 'Failed to access domain. Please try again.',
    ): void => {
      navigationGuard.guardedNavigate(async () => {
        try {
          if (targetDomainStatus === AssessmentDomainStatus.NOT_STARTED) {
            await startDomainMutation.mutateAsync({
              assessmentId,
              assessmentDomainId: targetDomainId,
            });
          }
          navigate(routes.assessmentDomain(assessmentId, targetDomainId));
        } catch (error) {
          console.error(`Failed to navigate (${navigationType}):`, error);
          const errorMessage = getLocalizedErrorMessage(error, t, errorFallback);
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      }, navigationType);
    },
    [
      assessmentId,
      enqueueSnackbar,
      navigate,
      navigationGuard,
      startDomainMutation,
      t,
    ],
  );

  const handleBack = useCallback(() => {
    if (!progressFlowViewModel || selectedIndex <= 1) return;
    const prevIndex = selectedIndex - 1;
    const prevDomain = findDomainByOrderIndex(assessment, prevIndex);
    const prevDomainId = prevDomain?.id;
    if (!prevDomainId) return;
    const prevDomainState = progressFlowViewModel.domainStates.get(prevIndex);
    navigateToDomain(
      prevDomainId,
      prevDomainState,
      'back',
      'Failed to access previous domain. Please try again.',
    );
  }, [
    assessment,
    navigateToDomain,
    progressFlowViewModel,
    selectedIndex,
  ]);

  const handleNodeClick = useCallback(
    (index: number) => {
      if (!assessment || !progressFlowViewModel) return;
      if (index === selectedIndex) return;

      const domainState = progressFlowViewModel.domainStates.get(index);
      const domain = findDomainByOrderIndex(assessment, index);
      const domainId = domain?.id;
      if (!domainId) return;

      if (!checkCanNavigateToDomain(domainState)) {
        enqueueSnackbar(t('app.assessment.workflow.notAccessible'), { variant: 'warning' });
        return;
      }
      navigateToDomain(domainId, domainState, 'domain');
    },
    [
      assessment,
      enqueueSnackbar,
      navigateToDomain,
      progressFlowViewModel,
      selectedIndex,
      t,
    ],
  );

  /**
   * Save current domain and advance. On the last domain this finishes the
   * assessment (navigates to the summary page) instead of advancing.
   */
  const handleSaveAndNext = useCallback(async () => {
    try {
      if (
        !assessmentId ||
        !domainDetailsViewModel?.id ||
        !assessment ||
        !progressFlowViewModel
      ) {
        return;
      }

      const isLastDomain = selectedIndex >= progressFlowViewModel.totalDomains;
      const currentDomainId = domainDetailsViewModel.id;
      const currentDomainStatus = domainDetailsViewModel.status;

      const saveValidation = validateDomainBeforeSave({
        domainStatus: currentDomainStatus,
        isApplicable,
        noEvidenceChecked,
        files,
        minRequiredDocuments: domainDetailsViewModel.minRequiredDocuments,
        maxRequiredDocuments: domainDetailsViewModel.maxRequiredDocuments,
      });

      if (!saveValidation.valid) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValidationError(t(saveValidation.errorKey as any, saveValidation.errorParams as any));
        return;
      }

      setValidationError(null);

      // Last domain: all previous must be completed before finishing.
      if (isLastDomain) {
        const allDomains = assessment.assessmentDomains || [];
        const previousDomains = allDomains.filter((d) => d.id !== currentDomainId);
        const allPreviousCompleted = previousDomains.every(
          (d) => d.status === AssessmentDomainStatus.COMPLETED,
        );

        if (!allPreviousCompleted) {
          enqueueSnackbar(t('app.assessment.workflow.notCompleted'), { variant: 'error' });
          return;
        }

        if (currentDomainStatus === AssessmentDomainStatus.IN_PROGRESS) {
          await saveDomainMutation.mutateAsync({
            params: { assessmentId, assessmentDomainId: currentDomainId },
            data: { isSkipped: noEvidenceChecked, isApplicable },
          });
        }

        if (currentDomainStatus === AssessmentDomainStatus.NOT_STARTED) {
          enqueueSnackbar(t('app.assessment.workflow.notStarted'), { variant: 'error' });
          return;
        }

        enqueueSnackbar(t('app.assessment.workflow.completed'), { variant: 'success' });
        navigate(routes.assessmentSummary(assessmentId));
        return;
      }

      // Regular domain: validate, save, then advance to next.
      const validation = validateNextDomain(
        selectedIndex,
        assessment,
        progressFlowViewModel.totalDomains,
      );

      if (!validation.isValid || !validation.nextDomainId) {
        console.error('Next domain validation failed:', validation.error);
        enqueueSnackbar(t('app.assessment.workflow.noNextDomain'), { variant: 'error' });
        return;
      }

      await saveDomainMutation.mutateAsync({
        params: { assessmentId, assessmentDomainId: currentDomainId },
        data: { isSkipped: noEvidenceChecked, isApplicable },
      });

      const { nextDomainId } = validation;
      const nextDomain = assessment.assessmentDomains?.find((d) => d.id === nextDomainId);
      const nextDomainStatus = nextDomain?.status;

      if (nextDomainStatus !== AssessmentDomainStatus.COMPLETED) {
        await startDomainMutation.mutateAsync({
          assessmentId,
          assessmentDomainId: nextDomainId,
        });
      }

      navigate(routes.assessmentDomain(assessmentId, nextDomainId));
    } catch (error) {
      console.error('Failed to save and proceed:', error);
    }
  }, [
    assessmentId,
    assessment,
    domainDetailsViewModel,
    progressFlowViewModel,
    selectedIndex,
    files,
    isApplicable,
    noEvidenceChecked,
    saveDomainMutation,
    startDomainMutation,
    setValidationError,
    enqueueSnackbar,
    navigate,
    t,
  ]);

  const handleResumeLater = useCallback(() => {
    navigationGuard.guardedNavigate(() => {
      navigate(routes.assessmentList());
    }, 'resume');
  }, [navigate, navigationGuard]);

  const handleResume = useCallback(() => {
    navigationGuard.guardedNavigate(async () => {
      if (!assessment?.assessmentDomains) return;

      // Prefer any IN_PROGRESS domain; otherwise start the first NOT_STARTED.
      const inProgressDomain = assessment.assessmentDomains.find(
        (domain) => domain.status === AssessmentDomainStatus.IN_PROGRESS,
      );
      if (inProgressDomain) {
        navigate(routes.assessmentDomain(assessmentId, inProgressDomain.id));
        return;
      }

      const firstNotStartedDomain = assessment.assessmentDomains
        .filter((domain) => domain.status === AssessmentDomainStatus.NOT_STARTED)
        .sort((a, b) => a.orderIndex - b.orderIndex)[0];

      await startDomainMutation.mutateAsync({
        assessmentId,
        assessmentDomainId: firstNotStartedDomain.id,
      });

      navigate(routes.assessmentDomain(assessmentId, firstNotStartedDomain.id));
    }, 'resumeNext');
  }, [
    assessment,
    assessmentId,
    navigate,
    navigationGuard,
    startDomainMutation,
  ]);

  /**
   * Navigate to next domain without saving (used when current domain is not
   * in progress and the next is reachable). Starts the next domain only when
   * no other domain is currently in progress.
   */
  const handleNext = useCallback(() => {
    if (!assessment) return;

    const nextIndex = selectedIndex + 1;
    const nextDomain = findDomainByOrderIndex(assessment, nextIndex);
    const nextDomainId = nextDomain?.id;

    if (!nextDomainId) {
      enqueueSnackbar(t('app.assessments.error.noNextDomain'), { variant: 'warning' });
      return;
    }

    if (!assessment?.assessmentDomains) {
      console.error('No domain details data found');
      return;
    }

    const isAnyDomainInprogressFound = assessment.assessmentDomains.some(
      (domain) => domain.status === AssessmentDomainStatus.IN_PROGRESS,
    );

    if (
      nextDomain.status === AssessmentDomainStatus.NOT_STARTED &&
      !isAnyDomainInprogressFound
    ) {
      navigationGuard.guardedNavigate(async () => {
        await startDomainMutation.mutateAsync({
          assessmentId,
          assessmentDomainId: nextDomainId,
        });
        navigate(routes.assessmentDomain(assessmentId, nextDomainId));
      }, 'resumeNext');
      return;
    }

    navigationGuard.guardedNavigate(async () => {
      navigate(routes.assessmentDomain(assessmentId, nextDomainId));
    }, 'next');
  }, [
    assessment,
    assessmentId,
    enqueueSnackbar,
    navigate,
    navigationGuard,
    selectedIndex,
    startDomainMutation,
    t,
  ]);

  /**
   * Re-open a COMPLETED domain for editing by starting it again (status
   * flips back to IN_PROGRESS server-side).
   */
  const handleEditDomain = useCallback(async () => {
    if (!assessmentId || !domainDetailsViewModel?.id) return;
    try {
      await startDomainMutation.mutateAsync({
        assessmentId,
        assessmentDomainId: domainDetailsViewModel.id,
      });
    } catch (error) {
      console.error(t('app.assessment.workflow.failedToStartEditingDomain'), error);
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to edit domain. Please try again.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [
    assessmentId,
    domainDetailsViewModel,
    enqueueSnackbar,
    startDomainMutation,
    t,
  ]);

  /**
   * Browser-back equivalent of `handleBack` that doesn't use the navigation
   * guard (the caller has already guarded). Falls through to the browser's
   * history when there's no previous domain.
   */
  const navigateToPreviousDomainWithStart = useCallback(async () => {
    if (selectedIndex > 0 && assessment) {
      const prevIndex = selectedIndex - 1;
      const prevDomain = findDomainByOrderIndex(assessment, prevIndex);
      const prevDomainId = prevDomain?.id;

      if (prevDomainId) {
        const prevDomainStatus = assessment.assessmentDomains?.find(
          (d) => d.id === prevDomainId,
        )?.status;

        if (prevDomainStatus === AssessmentDomainStatus.NOT_STARTED) {
          try {
            await startDomainMutation.mutateAsync({
              assessmentId,
              assessmentDomainId: prevDomainId,
            });
          } catch (error) {
            console.error('Failed to start previous domain:', error);
          }
        }

        navigate(routes.assessmentDomain(assessmentId, prevDomainId));
        return;
      }
    }

    navigate(-1);
  }, [selectedIndex, assessment, assessmentId, startDomainMutation, navigate]);

  return {
    handleBack,
    handleNodeClick,
    handleSaveAndNext,
    handleResume,
    handleResumeLater,
    handleNext,
    handleEditDomain,
    navigateToPreviousDomainWithStart,
    saveDomainMutation,
    startDomainMutation,
  };
};
